import type { FC } from 'react';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as GuiStateSelectors from 'app/selectors/guiStateSelectors.ts';
import { ListItem, ListItemText, ListItemIcon, IconButton, Collapse, Typography, Box } from '@mui/material';
import { useHistory } from 'react-router-dom';

import AddScenarioSection from './AddScenarioSection/index.ts';
import useStyles, { StyledScenarioListItemButton, StyledScenarioListItem } from './styles.ts';

import { isUserAnyAdminSelector } from 'app/selectors/userSelectors.ts';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ViewList from '@mui/icons-material/ViewList';
import SupportAgent from '@mui/icons-material/SupportAgent';
import ArrowForward from '@mui/icons-material/ArrowForward';

import { useTranslation } from 'react-i18next';
import type { I18nScenarioSectionNs } from '@/i18n/dictionaries/interfaces.ts';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import AppRoutes from 'app/utils/routes.ts';
import { GuiStateActions } from 'app/actions/guiState/guiStateAction.ts';
import { useRecentScenarios } from 'app/components/ScenariosBrowserPage/useRecentScenarios.ts';
import { useScenarioOpen } from 'app/components/ScenariosBrowserPage/useScenarioOpen.ts';

const ScenarioSection: FC = () => {
  const [translate] = useTranslation(I18nNamespace.ScenarioSection);
  const classes = useStyles();
  const dispatch = useDispatch();
  const sideBarOpenState = useSelector(GuiStateSelectors.sideBarStateSelector);
  const isAnyAdmin = useSelector(isUserAnyAdminSelector);

  const [open, setOpen] = useState(false);

  const recents = useRecentScenarios(5);
  const { openDefault } = useScenarioOpen();

  const history = useHistory();

  // Clicking the row navigates to the Scenarios page (and reveals the recent list);
  // the chevron toggles the recent list without navigating.
  const handleNavigate = () => {
    if (!sideBarOpenState) {
      dispatch(GuiStateActions.sideMenuOpen(true));
    }
    setOpen(true);
    history.push(AppRoutes.Scenarios);
  };

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  return (
    <>
      <ListItem
        disablePadding
        secondaryAction={
          sideBarOpenState ? (
            <IconButton
              edge="end"
              size="small"
              onClick={handleToggle}
              aria-expanded={open}
              aria-label={translate(nameof.full<I18nScenarioSectionNs>((l) => l.browser.toggleRecent))}
            >
              {open ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          ) : undefined
        }
      >
        <StyledScenarioListItemButton data-cy="scenarioOpenListButton" onClick={handleNavigate}>
          <ListItemIcon sx={{ minWidth: 24 }}>
            <ViewList classes={{ root: classes.black }} />
          </ListItemIcon>
          {sideBarOpenState && (
            <ListItemText primary={translate(nameof.full<I18nScenarioSectionNs>((l) => l.scenarios))} />
          )}
        </StyledScenarioListItemButton>
      </ListItem>
      {sideBarOpenState && (
        <Collapse in={open} timeout="auto" unmountOnExit classes={{ entered: classes.colapsedEntered }}>
          {isAnyAdmin && <AddScenarioSection />}

          {recents.length > 0 && (
            <Box sx={{ pt: 0.5 }}>
              <Typography
                variant="overline"
                sx={{ display: 'block', px: 2, pt: 1, color: 'grey.500', letterSpacing: '0.08em' }}
              >
                {translate(nameof.full<I18nScenarioSectionNs>((l) => l.browser.recent))}
              </Typography>
              {recents.map((scene) => (
                <StyledScenarioListItem
                  key={scene.id}
                  onClick={() => openDefault(scene, false)}
                  title={scene.name ?? ''}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: 'grey.400' }}>
                    <SupportAgent fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={scene.name} slotProps={{ primary: { noWrap: true, variant: 'body2' } }} />
                </StyledScenarioListItem>
              ))}
            </Box>
          )}

          <StyledScenarioListItem onClick={handleNavigate}>
            <ListItemIcon sx={{ minWidth: 32, color: 'grey.500' }}>
              <ArrowForward fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={translate(nameof.full<I18nScenarioSectionNs>((l) => l.browser.viewAll))}
              slotProps={{ primary: { variant: 'body2', sx: { fontWeight: 500 } } }}
            />
          </StyledScenarioListItem>
        </Collapse>
      )}
    </>
  );
};

export default ScenarioSection;
